import React, { useState, useEffect } from 'react';
import { Document, Page } from 'react-pdf';
import AutoTranslateText from '../../components/AutoTranslateText'; // ✅ Import ajouté

// ✅ CORRECTION : Import des fichiers CSS manquants pour react-pdf
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

const PDFViewer: React.FC<{
  url: string;
  width?: number; // ✅ Ajout du '?' pour le rendre optionnel
  height?: number; // ✅ Ajout du '?' pour le rendre optionnel
  onLoadError: (error: string) => void;
  onLoadSuccess: () => void;
}> = ({ url, width, height, onLoadError, onLoadSuccess }) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    onLoadSuccess();
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('Erreur chargement PDF:', error);
    onLoadError(error.message);
  };

  const goToPrevPage = () => setPageNumber(page => Math.max(1, page - 1));
  const goToNextPage = () => setPageNumber(page => Math.min(numPages, page + 1));
  const zoomIn = () => setScale(scale => Math.min(2.0, scale + 0.2));
  const zoomOut = () => setScale(scale => Math.max(0.5, scale - 0.2));

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;
      switch (event.key) {
        case 'ArrowLeft': event.preventDefault(); goToPrevPage(); break;
        case 'ArrowRight': event.preventDefault(); goToNextPage(); break;
        case '+': case '=': event.preventDefault(); zoomIn(); break;
        case '-': event.preventDefault(); zoomOut(); break;
        case 'Home': event.preventDefault(); setPageNumber(1); break;
        case 'End': event.preventDefault(); setPageNumber(numPages); break;
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [numPages]);

  return (
    <div className="pdf-viewer-container" style={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <style>{`
        .pdf-controls { background: #f8f9fa; border-bottom: 1px solid #dee2e6; flex-shrink: 0; }
        /* ✅ Ajout de display: flex et justify-content: center pour centrer le document s'il est plus petit */
        .pdf-document-wrapper { background-color: #e9ecef; padding: 1rem; overflow: auto; flex-grow: 1; display: flex; justify-content: center; } 
        .react-pdf__Page { margin: 0 auto 1rem auto; box-shadow: 0 0 8px rgba(0,0,0,0.15); }
        .cursor-pointer { cursor: pointer; }
        .pdf-viewer-container:fullscreen { background-color: #333; }
      `}</style>
      <div className="pdf-controls p-2">
        <div className="d-flex justify-content-center align-items-center flex-wrap gap-3">
          <div className="btn-group btn-group-sm">
            <button className="btn btn-outline-secondary" onClick={goToPrevPage} disabled={pageNumber <= 1} title="Page précédente (←)">
              ←
            </button>
            <button className="btn btn-outline-secondary" onClick={goToNextPage} disabled={pageNumber >= numPages} title="Page suivante (→)">
              →
            </button>
          </div>
          <span className="small">
            <AutoTranslateText sourceLang="fr">Page</AutoTranslateText> {pageNumber} / {numPages}
          </span>
          <div className="btn-group btn-group-sm">
            <button className="btn btn-outline-secondary" onClick={zoomOut} disabled={scale <= 0.5} title="Zoom arrière (-)">-</button>
            <button className="btn btn-outline-secondary" onClick={() => setScale(1.0)} title="Zoom 100%">{Math.round(scale * 100)}%</button>
            <button className="btn btn-outline-secondary" onClick={zoomIn} disabled={scale >= 2.0} title="Zoom avant (+)">+</button>
          </div>
        </div>
      </div>
      <div className="pdf-document-wrapper">
        <Document 
          file={url} 
          onLoadSuccess={onDocumentLoadSuccess} 
          onLoadError={onDocumentLoadError} 
          loading={
            <div className="text-center p-5">
              <AutoTranslateText sourceLang="fr">Chargement...</AutoTranslateText>
            </div>
          }
        >
          {/* ✅ CORRECTION : Application du scale sur la largeur fixe pour permettre le Zoom */}
          <Page 
             pageNumber={pageNumber} 
             // Si une largeur est fournie (mode Fit), on la multiplie par le scale pour zoomer
             // Sinon, on laisse react-pdf gérer le scale natif via la prop 'scale'
             width={width ? (Math.max(300, width - 40) * scale) : undefined}
             scale={width ? undefined : scale}
             renderTextLayer={false} 
             className="shadow-sm"
          />
        </Document>
      </div>
    </div>
  );
};

export default PDFViewer;